import React, { useState, useEffect } from 'react';
import { X, Car, Clock, ChevronDown } from 'lucide-react';
import { Resource, getResources } from '../lib/resources';
import { useBusiness } from '../contexts/BusinessContext';
import { createBooking } from '../lib/bookings';
import { supabase } from '../lib/supabase';
import CreateScheduleEditor from './booking/CreateScheduleEditor';
import SelectResourceModal from './manage/SelectResourceModal';
import { Customer, getCustomer } from '../lib/customers';
import { Proposal, ProposalService } from '../lib/proposals';
import EditCustomerModal from './manage/EditCustomerModal';
import RichTextDescription from './RichTextDescription';
import { triggerBookingAutomations } from '../lib/automationsV2';

interface ConvertToBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  proposal: Proposal | null;
  onSuccess: () => void;
}

export default function ConvertToBookingModal({ 
  isOpen, 
  onClose,
  proposal,
  onSuccess
}: ConvertToBookingModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditCustomerModalOpen, setIsEditCustomerModalOpen] = useState(false);
  const [isSelectResourceModalOpen, setIsSelectResourceModalOpen] = useState(false);
  const [scheduleChanged, setScheduleChanged] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedResources, setSelectedResources] = useState<Resource[]>([]);
  const [dropOffDate, setDropOffDate] = useState<string>('');
  const [dropOffTime, setDropOffTime] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [startTime, setStartTime] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');
  const [totalDuration, setTotalDuration] = useState<number>(0);

  // Generate time options in 30 minute increments
  const timeOptions = Array.from({ length: 48 }, (_, i) => {
    const hour = Math.floor(i / 2);
    const minute = i % 2 === 0 ? '00' : '30';
    const ampm = hour >= 12 ? 'pm' : 'am';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return {
      value: `${hour.toString().padStart(2, '0')}:${minute}`,
      label: `${displayHour}:${minute} ${ampm}`
    };
  });

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      loadCustomer();

      // Calculate total duration from approved offer services
      const approvedOffer = proposal?.offers?.find(o => o.status === 'approved');
      if (approvedOffer) {
        const duration = approvedOffer.services?.reduce((total, service) => 
          total + service.duration_minutes, 0) || 0;
        setTotalDuration(duration);
      }
    }
  }, [isOpen, selectedBusiness?.id]);

  const loadCustomer = async () => {
    if (!proposal?.customer_id) return;

    try {
      const data = await getCustomer(proposal.customer_id);
      setSelectedCustomer(data);
    } catch (err) {
      console.error('Error loading customer:', err);
      setError(err instanceof Error ? err.message : 'Failed to load customer');
    }
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setDropOffDate('');
      setDropOffTime('');
      setStartDate('');
      setStartTime('');
      setEndDate('');
      setEndTime('');
      setSelectedResources([]);
    }
  };

  const handleCreateBooking = async () => {
    if (!selectedBusiness?.id || !selectedCustomer?.id || !proposal?.offers || !supabase) {
      setError('Missing required data');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get approved offer
      const approvedOffer = proposal.offers.find(o => o.status === 'approved');
      if (!approvedOffer || !approvedOffer.services) {
        throw new Error('No approved offer or services found');
      }

      // Filter for only selected services from the approved offer
      const selectedServices = approvedOffer.services.filter(service => {
        // Debugging - log the full service object to see all properties
        console.log("Service object keys:", Object.keys(service));
        console.log("Full service object:", service);
        
        // Try multiple ways to check for optional/selectable state
        const isSelectable = 
          (service as any).isSelectable === true || 
          (service as any).is_selectable === true;
        
        // Try multiple ways to check for selection state  
        const isSelected = 
          (service as any).is_selected === true;
        
        // Check for "Optional" in the service name
        const isOptionalByName = service.name.toLowerCase().includes('optional');
        
        console.log(`Service ${service.name}: isSelectable=${isSelectable}, isSelected=${isSelected}, isOptionalByName=${isOptionalByName}, include=${!isSelectable || (isSelectable && isSelected)}`);
        
        // If this is an optional service by name but isSelectable flag isn't set,
        // treat it as an optional service
        if (isOptionalByName && !isSelectable) {
          console.log(`Service '${service.name}' has 'Optional' in name but no isSelectable flag`);
          return isSelected; // Only include if it's selected
        }
        
        // Otherwise apply the normal rules: 
        // Include if either not optional or (optional AND selected)
        return !isSelectable || (isSelectable && isSelected);
      });
      
      if (selectedServices.length === 0) {
        throw new Error('No selected services found in the approved offer');
      }

      // Group services by vehicle from approved offer
      const servicesByVehicle = selectedServices.reduce((acc, service) => {
        if (!service.vehicle_id || !service.service_id || !service.name) {
          throw new Error('Invalid service data: missing required fields');
        }
        const vehicleId = service.vehicle_id;
        if (!acc[vehicleId]) {
          acc[vehicleId] = [];
        }
        acc[vehicleId].push({
          ...service,
          name: service.name.trim() || 'Unnamed Service', // Ensure name is trimmed and not empty
          description: service.description?.trim() || '',
          price: service.price || 0,
          tax_exempt: service.tax_exempt || false,
          tax_rate: service.tax_rate || 5,
          duration_minutes: service.duration_minutes || 60
        });
        return acc;
      }, {} as Record<string, ProposalService[]>);

      // Validate required fields
      if (!dropOffDate || !dropOffTime || !startDate || !startTime || !endDate || !endTime) {
        throw new Error('Please fill in all required schedule fields');
      }

      if (!selectedResources.length) {
        throw new Error('Please select at least one resource');
      }

      // Group services by vehicle from approved offer
      const vehicles = Object.entries(servicesByVehicle).map(([vehicleId, services]) => ({
        vehicle_id: vehicleId,
        services: services.map(service => ({
          service_id: service.service_id,
          name: service.name || 'Unnamed Service',
          description: service.description || '',
          price: Number(service.price) || 0,
          tax_exempt: Boolean(service.tax_exempt),
          tax_rate: Number(service.tax_rate) || 5,
          duration_minutes: Number(service.duration_minutes) || 60
        }))
      }));

      // Create the booking using the createBooking function
      const newBooking = await createBooking({
        business_id: selectedBusiness.id,
        customer_id: selectedCustomer.id,
        title: proposal.title || 'New Booking',
        status: 'scheduled',
        drop_off_date: dropOffDate,
        drop_off_time: dropOffTime,
        start_date: startDate,
        start_time: startTime,
        end_date: endDate,
        end_time: endTime,
        vehicles,
        resources: selectedResources.map(resource => ({
          resource_id: resource.id,
          role: 'technician'
        }))
      });

      // 🎯 Trigger booking automations after successful conversion
      if (selectedBusiness?.id && newBooking) {
        try {
          // Calculate grand total from selected services
          const grandTotal = selectedServices.reduce((total, service) => total + (service.price || 0), 0);
          
          // Prepare booking data for automation trigger
          const realBookingData = {
            booking_id: newBooking.id || Date.now(),
            title: proposal.title || 'New Booking',
            customer: {
              full_name: `${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
              phone: selectedCustomer.phone || undefined,
              email: selectedCustomer.email || undefined
            },
            schedule: {
              drop_off_date: dropOffDate,
              drop_off_time: dropOffTime,
              start_date: startDate,
              start_time: startTime,
              end_date: endDate,
              end_time: endTime
            },
            services: selectedServices.map(service => ({
              name: service.name,
              price: service.price,
              duration_minutes: service.duration_minutes,
              vehicle: {
                id: service.vehicle_id,
                make: service.vehicle_make || '',
                model: service.vehicle_model || '',
                year: service.vehicle_year || ''
              }
            })),
            grand_total: grandTotal,
            status: 'scheduled',
            resources: selectedResources.map(resource => ({
              name: `${resource.first_name} ${resource.last_name}`,
              email: resource.email,
              phone: resource.phone
            })),
            vehicles: (() => {
              const vehicles = selectedServices.map(service => ({
                make: service.vehicle_make || '',
                model: service.vehicle_model || '',
                year: service.vehicle_year || 0,
                color: service.vehicle_color || '',
                display: `${service.vehicle_year || ''} ${service.vehicle_make || ''} ${service.vehicle_model || ''}`.trim()
              }));
              
              // Remove duplicates based on make/model/year combination
              const uniqueVehicles = vehicles.filter((vehicle, index, self) => 
                index === self.findIndex(v => v.make === vehicle.make && v.model === vehicle.model && v.year === vehicle.year)
              );
              
              return uniqueVehicles;
            })(),
            source: 'proposal_conversion'
          };

          console.log('🎯 PROPOSAL CONVERSION: Triggering booking automations with data:', realBookingData);
          await triggerBookingAutomations(selectedBusiness.id, 'created', realBookingData);
          console.log('🎯 PROPOSAL CONVERSION: Booking automations triggered successfully');
        } catch (automationError) {
          console.error('❌ PROPOSAL CONVERSION: Failed to trigger automations:', automationError);
          // Don't fail the entire booking process if automation fails
        }
      }

      // Update proposal status to booked
      if (proposal.id) {
        const { error: updateError } = await supabase
          .from('proposals')
          .update({ status: 'booked' })
          .eq('id', proposal.id);

        if (updateError) throw updateError;
      }

      onSuccess();
    } catch (err) {
      console.error('Error creating booking:', err);
      setError(err instanceof Error ? err.message : 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;
  if (!proposal) return null;

  return (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[20000]`}
      onClick={onClose}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          <div className="px-6 py-4 border-b border-[#2f2f2f] bg-[#1a1a1a] flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Convert to Booking</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-300">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-8">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              {/* Customer Info */}
              {selectedCustomer && (
                <div className="mb-8">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-sm font-medium text-gray-900">Customer</h3>
                    <button
                      onClick={() => setIsEditCustomerModalOpen(true)}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      Edit
                    </button>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center space-x-3">
                      <div className="h-10 w-10 rounded-full bg-black flex items-center justify-center text-xs font-medium text-white">
                        {selectedCustomer.first_name[0]}{selectedCustomer.last_name[0]}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {selectedCustomer.first_name} {selectedCustomer.last_name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {selectedCustomer.email}
                        </div>
                        {selectedCustomer.phone && (
                          <div className="text-xs text-gray-500">
                            {selectedCustomer.phone}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-6">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                    {error}
                  </div>
                )}

                {/* Services Summary */}
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-sm font-medium text-gray-900">Services</h3>
                  </div>
                  <div className="space-y-4">
                    {proposal.offers?.find(o => o.status === 'approved')?.services
                      ?.filter(service => {
                        // Try multiple ways to check for optional/selectable state
                        const isSelectable = 
                          (service as any).isSelectable === true || 
                          (service as any).is_selectable === true;
                        
                        // Try multiple ways to check for selection state  
                        const isSelected = 
                          (service as any).is_selected === true;
                        
                        // Check for "Optional" in the service name
                        const isOptionalByName = service.name.toLowerCase().includes('optional');
                        
                        console.log(`Service ${service.name}: isSelectable=${isSelectable}, isSelected=${isSelected}, isOptionalByName=${isOptionalByName}, include=${!isSelectable || (isSelectable && isSelected)}`);
                        
                        // If this is an optional service by name but isSelectable flag isn't set,
                        // treat it as an optional service
                        if (isOptionalByName && !isSelectable) {
                          console.log(`Service '${service.name}' has 'Optional' in name but no isSelectable flag`);
                          return isSelected; // Only include if it's selected
                        }
                        
                        return !isSelectable || (isSelectable && isSelected);
                      })
                      .map((service, index) => (
                      <div key={service.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <div className="flex items-center">
                              <h4 className="text-sm font-medium text-gray-900">{service.name}</h4>
                              {(service as any).isSelectable && (
                                <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                                  Optional (Selected)
                                </span>
                              )}
                            </div>
                            <RichTextDescription description={service.description} className="text-sm text-gray-500" />
                          </div>
                          <div className="text-sm font-medium text-gray-900">
                            ${service.price.toLocaleString()}
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center text-xs text-gray-500">
                            <Clock className="h-3.5 w-3.5 mr-1" />
                            {service.duration_minutes >= 1440 
                              ? `${Math.round(service.duration_minutes / 1440)} days`
                              : `${Math.round(service.duration_minutes / 60)} hours`
                            }
                          </div>
                          <div className="flex items-center text-xs text-gray-500">
                            <Car className="h-3.5 w-3.5 mr-1 text-gray-400" />
                            Vehicle ID: {service.vehicle_id}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Schedule Section */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-4">Schedule</h3>
                  <CreateScheduleEditor
                    dropOffDate={dropOffDate}
                    dropOffTime={dropOffTime}
                    startDate={startDate}
                    startTime={startTime}
                    endDate={endDate}
                    endTime={endTime}
                    serviceDuration={totalDuration}
                    onDateTimeChange={(type, date, time) => {
                      switch (type) {
                        case 'dropOff':
                          setDropOffDate(date);
                          setDropOffTime(time);
                          break;
                        case 'start':
                          setStartDate(date);
                          setStartTime(time);
                          break;
                        case 'end':
                          setEndDate(date);
                          setEndTime(time);
                          break;
                      }
                    }}
                    disabled={loading}
                  />
                </div>

                {/* Resources Section */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-4">Resources</h3>
                  {selectedResources.length > 0 ? (
                    <div className="space-y-3">
                      {selectedResources.map((resource) => (
                        <div key={resource.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center space-x-3">
                            <div className="h-10 w-10 rounded-full bg-black flex items-center justify-center text-sm font-medium text-white">
                              {resource.first_name[0]}{resource.last_name[0]}
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {resource.first_name} {resource.last_name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {resource.email}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => setSelectedResources(prev => prev.filter(r => r.id !== resource.id))}
                            className="text-xs text-red-600 hover:text-red-800"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => setIsSelectResourceModalOpen(true)}
                        className="w-full mt-4 flex items-center justify-center px-4 py-4 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:text-gray-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                      >
                        Add Another Resource
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setIsSelectResourceModalOpen(true)}
                      className="w-full px-4 py-4 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:text-gray-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    >
                      Add Resources
                    </button>
                  )}
                </div>

              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-200 bg-white">
            <button 
              onClick={handleCreateBooking}
              disabled={loading || !dropOffDate || !dropOffTime || !startDate || !startTime || !endDate || !endTime}
              className="w-full px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Booking'}
            </button>
            {(!dropOffDate || !dropOffTime || !startDate || !startTime || !endDate || !endTime) && (
              <p className="mt-2 text-xs text-center text-gray-500">Please select all required dates and times</p>
            )}
          </div>
        </div>
      </div>

      <EditCustomerModal
        isOpen={isEditCustomerModalOpen}
        onClose={() => setIsEditCustomerModalOpen(false)}
        onSuccess={(updatedCustomer) => {
          setSelectedCustomer(updatedCustomer);
          setIsEditCustomerModalOpen(false);
        }}
        customer={selectedCustomer}
      />
      <SelectResourceModal
        isOpen={isSelectResourceModalOpen}
        onClose={() => setIsSelectResourceModalOpen(false)}
        onSelect={(resource) => {
          // Check if resource is already selected
          if (!selectedResources.some(r => r.id === resource.id)) {
            setSelectedResources(prev => [...prev, resource]);
          }
          setIsSelectResourceModalOpen(false);
        }}
        selectedResources={selectedResources}
      />

    </div>
  );
}